import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { AgentInstructions } from "../../types";

/**
 * What save_agent_instructions() answers. `saved` is false when somebody —
 * an agent, or this person on another device — saved after `expectedVersion`
 * was loaded; the rest is then what is stored now, not what was sent.
 */
export interface SaveAgentInstructionsResult extends AgentInstructions {
  saved: boolean;
}

/**
 * Saves the whole text, but only over the version the editor loaded, so a
 * rule an agent added meanwhile is never silently erased.
 */
export function useSaveAgentInstructions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      expectedVersion,
    }: {
      body: string;
      expectedVersion: number;
    }): Promise<SaveAgentInstructionsResult> => {
      const { data, error } = await supabase.rpc("save_agent_instructions", {
        p_body: body,
        p_expected_version: expectedVersion,
      });
      if (error) throw error;
      return data as SaveAgentInstructionsResult;
    },

    onSuccess: ({ body, version, changed_by, updated_at }) => {
      queryClient.setQueryData<AgentInstructions>(queryKeys.agentInstructions, {
        body,
        version,
        changed_by,
        updated_at,
      });
    },

    onError: (err) => {
      Sentry.captureException(err, {
        extra: { mutation: "useSaveAgentInstructions" },
      });
      toast.error("Could not save the agent instructions. Please try again.");
    },
  });
}
